"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ScoreRow } from "@/lib/leaderboard";
import PlayerName from "@/components/PlayerName";

interface RunDetailModalProps {
  entry: ScoreRow | null;
  onClose: () => void;
}

// A large, centered window showing a single leaderboard entry's full run — every
// wheel they rolled, in order, plus their alignment / crown / score / rank. Reads
// straight from the row's stored `rolled_results`, so it works for existing
// entries too (every submitted score has always saved its rolls).
export default function RunDetailModal({ entry, onClose }: RunDetailModalProps) {
  // Close on Escape.
  useEffect(() => {
    if (!entry) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entry, onClose]);

  if (!entry || typeof document === "undefined") return null;

  const isVillain = entry.alignment.toLowerCase().includes("villain");
  const rolls = entry.rolled_results ?? [];

  // Rendered through a portal to document.body so it's always centered on the
  // viewport and never clipped by a transformed/overflow-hidden ancestor (e.g.
  // the leaderboard drawer, which is translated and clips its contents).
  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-fade-in flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-accent/40 bg-background shadow-[0_0_60px_rgba(0,0,0,0.7)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-6 py-4">
          <div className="min-w-0">
            <div className="text-lg font-extrabold">
              <PlayerName
                name={entry.name}
                title={entry.title}
                color={entry.color}
                nameClassName="text-white"
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full border px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-widest ${
                  isVillain
                    ? "border-red-500/50 text-red-400"
                    : "border-accent text-accent"
                }`}
              >
                {entry.alignment}
              </span>
              {entry.has_crown && (
                <span className="rounded-full border border-yellow-400/60 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-widest text-yellow-300">
                  👑 Crowned
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/20 text-white/60 transition hover:border-accent hover:text-accent"
          >
            ✕
          </button>
        </div>

        {/* Score */}
        <div className="shrink-0 border-b border-white/10 px-6 py-4 text-center">
          <div className="text-[0.65rem] font-bold uppercase tracking-[0.3em] text-white/40">
            Power Level
          </div>
          <div className="mt-1 text-[clamp(2rem,8vw,3.25rem)] font-extrabold leading-none">
            {entry.score.toLocaleString()}
          </div>
          <div className="mt-3 inline-block rounded-full bg-accent px-5 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
            {entry.rank_label}
          </div>
        </div>

        {/* Full roll breakdown */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <h3 className="mb-3 text-center text-xs font-bold uppercase tracking-[0.3em] text-white/40">
            The Whole Run
          </h3>
          {rolls.length === 0 ? (
            <p className="py-6 text-center text-sm text-white/40">
              No roll details were saved for this run.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {rolls.map((r, i) => (
                <div
                  key={`${r.wheelName}-${i}`}
                  className="flex items-center justify-between gap-2 rounded-lg bg-black/30 px-4 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-[0.65rem] uppercase tracking-widest text-white/40">
                      {r.wheelName}
                    </div>
                    <div className="truncate font-semibold">{r.option.name}</div>
                  </div>
                  <div
                    className={`shrink-0 font-mono font-bold ${
                      r.option.value >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {r.option.value >= 0 ? "+" : ""}
                    {r.option.value.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
